/**
 * Edge Routing
 *
 * Calculates SVG paths for edges connecting nodes in the flow diagram.
 * Uses cubic Bezier curves for smooth visual flow.
 * Implements edge slot allocation for proper vertical distribution.
 */

import type { FlowEdge, PositionedNode, PositionedEdge } from '../types';
import { EDGE, COLORS } from '../constants';
import { findPositionedNode } from './layoutEngine';

/**
 * Create a cubic Bezier path between two points
 * Uses horizontal control points for smooth S-curves
 */
export function createEdgePath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number
): string {
  // Control points at the horizontal midpoint
  const midX = (sourceX + targetX) / 2;

  return `M ${sourceX} ${sourceY} C ${midX} ${sourceY}, ${midX} ${targetY}, ${targetX} ${targetY}`;
}

/**
 * Calculate stroke width based on edge value relative to max value
 * Ensures minimum visibility while showing proportional differences
 */
export function calculateStrokeWidth(value: number, maxValue: number): number {
  if (maxValue === 0) return EDGE.MIN_STROKE_WIDTH;

  const proportional = (value / maxValue) * EDGE.MAX_STROKE_WIDTH;
  return Math.max(EDGE.MIN_STROKE_WIDTH, Math.min(EDGE.MAX_STROKE_WIDTH, proportional));
}

/**
 * Get the stroke color for an edge based on its type
 */
export function getEdgeColor(type: FlowEdge['type']): string {
  switch (type) {
    case 'income':
      return COLORS.edge.income;
    case 'expense':
      return COLORS.edge.expense;
    case 'transfer':
      return COLORS.edge.transfer;
    default:
      return COLORS.edge.expense;
  }
}

/**
 * Get the hover stroke color for an edge based on its type
 */
export function getEdgeHoverColor(type: FlowEdge['type']): string {
  switch (type) {
    case 'income':
      return COLORS.edgeHover.income;
    case 'expense':
      return COLORS.edgeHover.expense;
    case 'transfer':
      return COLORS.edgeHover.transfer;
    default:
      return COLORS.edgeHover.expense;
  }
}

// ============================================================================
// Edge Slot Allocation
// ============================================================================

/**
 * Group edges by their source or target node
 */
function groupEdgesByNode(
  edges: FlowEdge[],
  side: 'source' | 'target'
): Map<string, FlowEdge[]> {
  const grouped = new Map<string, FlowEdge[]>();
  for (const edge of edges) {
    const nodeId = side === 'source' ? edge.source : edge.target;
    const existing = grouped.get(nodeId) || [];
    existing.push(edge);
    grouped.set(nodeId, existing);
  }
  return grouped;
}

/**
 * Calculate Y offset slots for edges at a node using padding approach
 * Returns a map of edgeId -> Y position for the given side (source or target)
 *
 * Edges are sorted by the Y position of their connected node to create
 * visually pleasing flows. Edges are stacked directly adjacent (no gaps)
 * with padding at top and bottom to center them on the node.
 */
function calculateEdgeSlots(
  edges: FlowEdge[],
  node: PositionedNode,
  allNodes: PositionedNode[],
  side: 'source' | 'target',
  strokeWidths: Map<string, number>
): Map<string, number> {
  const slots = new Map<string, number>();

  if (edges.length === 0) return slots;

  if (edges.length === 1) {
    // Single edge - use center
    const singleEdge = edges[0];
    if (singleEdge) {
      slots.set(singleEdge.id, node.y + node.height / 2);
    }
    return slots;
  }

  // Sort edges by the Y position of their connected node for visual flow
  const sortedEdges = [...edges].sort((a, b) => {
    const aConnectedId = side === 'source' ? a.target : a.source;
    const bConnectedId = side === 'source' ? b.target : b.source;
    const aNode = allNodes.find((n) => n.node.id === aConnectedId);
    const bNode = allNodes.find((n) => n.node.id === bConnectedId);
    return (aNode?.y ?? 0) - (bNode?.y ?? 0);
  });

  // Calculate total stroke height for all edges at this node
  let totalStrokeHeight = 0;
  for (const edge of sortedEdges) {
    const strokeWidth = strokeWidths.get(edge.id) ?? EDGE.MIN_STROKE_WIDTH;
    totalStrokeHeight += strokeWidth;
  }

  // Calculate padding to center the stacked edges on the node
  // If total stroke height exceeds node height, use minimal padding
  const padding = Math.max(0, (node.height - totalStrokeHeight) / 2);

  // Position edges cumulatively, stacked directly adjacent (no gaps)
  let currentY = node.y + padding;
  for (const edge of sortedEdges) {
    const strokeWidth = strokeWidths.get(edge.id) ?? EDGE.MIN_STROKE_WIDTH;
    // Connect at the center of this edge's stroke
    const centerY = currentY + strokeWidth / 2;
    slots.set(edge.id, centerY);
    // Move to next position (directly adjacent, no gap)
    currentY += strokeWidth;
  }

  return slots;
}

/**
 * Pre-calculate stroke widths for all edges
 * Used by slot calculation to stack edges with correct sizing
 */
export function calculateAllStrokeWidths(edges: FlowEdge[]): Map<string, number> {
  const strokeWidths = new Map<string, number>();
  if (edges.length === 0) return strokeWidths;

  const maxValue = Math.max(...edges.map((e) => e.value));

  for (const edge of edges) {
    const strokeWidth = calculateStrokeWidth(edge.value, maxValue);
    strokeWidths.set(edge.id, strokeWidth);
  }

  return strokeWidths;
}

/**
 * Calculate the minimum height required for each node based on connected edges
 *
 * This is part of the two-pass layout algorithm to prevent edge overflow.
 * For each node, we sum the stroke widths of all edges that connect to it
 * (either as source or target). The node must be at least this tall to
 * accommodate all connected edges without overflow.
 *
 * @param edges - All flow edges in the diagram
 * @param strokeWidths - Pre-calculated stroke widths from calculateAllStrokeWidths()
 * @returns Map of nodeId -> minimum height required to fit all connected edges
 */
export function calculateMinNodeHeightsFromEdges(
  edges: FlowEdge[],
  strokeWidths: Map<string, number>
): Map<string, number> {
  const minHeights = new Map<string, number>();

  if (edges.length === 0) return minHeights;

  // Group edges by source and target nodes
  const edgesBySource = groupEdgesByNode(edges, 'source');
  const edgesByTarget = groupEdgesByNode(edges, 'target');

  // Calculate minimum height for each source node (outgoing edges)
  for (const [nodeId, nodeEdges] of edgesBySource) {
    let totalStrokeHeight = 0;
    for (const edge of nodeEdges) {
      const strokeWidth = strokeWidths.get(edge.id) ?? EDGE.MIN_STROKE_WIDTH;
      totalStrokeHeight += strokeWidth;
    }
    // Update to the larger of existing or new value
    const existingHeight = minHeights.get(nodeId) ?? 0;
    minHeights.set(nodeId, Math.max(existingHeight, totalStrokeHeight));
  }

  // Calculate minimum height for each target node (incoming edges)
  for (const [nodeId, nodeEdges] of edgesByTarget) {
    let totalStrokeHeight = 0;
    for (const edge of nodeEdges) {
      const strokeWidth = strokeWidths.get(edge.id) ?? EDGE.MIN_STROKE_WIDTH;
      totalStrokeHeight += strokeWidth;
    }
    // Update to the larger of existing or new value
    const existingHeight = minHeights.get(nodeId) ?? 0;
    minHeights.set(nodeId, Math.max(existingHeight, totalStrokeHeight));
  }

  return minHeights;
}

/**
 * Calculate all edge Y positions for proper vertical distribution
 *
 * This function pre-calculates the Y positions where each edge should
 * connect to its source and target nodes. Edges are stacked directly
 * adjacent (no gaps) with padding to center them on the node.
 *
 * Also returns pre-calculated stroke widths for consistent rendering.
 */
export function calculateAllEdgeSlots(
  edges: FlowEdge[],
  allPositionedNodes: PositionedNode[]
): {
  sourceSlots: Map<string, number>;
  targetSlots: Map<string, number>;
  strokeWidths: Map<string, number>;
} {
  const sourceSlots = new Map<string, number>();
  const targetSlots = new Map<string, number>();

  // Pre-calculate stroke widths for all edges
  const strokeWidths = calculateAllStrokeWidths(edges);

  // Group edges by source and target nodes
  const bySource = groupEdgesByNode(edges, 'source');
  const byTarget = groupEdgesByNode(edges, 'target');

  // Calculate slots for each source node
  for (const [nodeId, nodeEdges] of bySource) {
    const node = allPositionedNodes.find((pn) => pn.node.id === nodeId);
    if (node) {
      const slots = calculateEdgeSlots(nodeEdges, node, allPositionedNodes, 'source', strokeWidths);
      slots.forEach((y, edgeId) => sourceSlots.set(edgeId, y));
    }
  }

  // Calculate slots for each target node
  for (const [nodeId, nodeEdges] of byTarget) {
    const node = allPositionedNodes.find((pn) => pn.node.id === nodeId);
    if (node) {
      const slots = calculateEdgeSlots(nodeEdges, node, allPositionedNodes, 'target', strokeWidths);
      slots.forEach((y, edgeId) => targetSlots.set(edgeId, y));
    }
  }

  return { sourceSlots, targetSlots, strokeWidths };
}

/**
 * Route all edges given positioned nodes
 * Calculates paths and stroke widths for each edge
 *
 * Uses edge slot allocation to distribute edges vertically along node edges.
 * Edges are stacked directly adjacent with padding to center them on nodes.
 *
 * @param edges - The flow edges to route
 * @param allPositionedNodes - All positioned nodes in the diagram
 * @param sourceSlots - Optional pre-calculated source Y positions (edge ID -> Y)
 * @param targetSlots - Optional pre-calculated target Y positions (edge ID -> Y)
 * @param strokeWidths - Optional pre-calculated stroke widths (edge ID -> width)
 */
export function routeEdges(
  edges: FlowEdge[],
  allPositionedNodes: PositionedNode[],
  sourceSlots?: Map<string, number>,
  targetSlots?: Map<string, number>,
  strokeWidths?: Map<string, number>
): PositionedEdge[] {
  if (edges.length === 0) return [];

  // Calculate slots and stroke widths if not provided
  if (!sourceSlots || !targetSlots || !strokeWidths) {
    const slots = calculateAllEdgeSlots(edges, allPositionedNodes);
    sourceSlots = slots.sourceSlots;
    targetSlots = slots.targetSlots;
    strokeWidths = slots.strokeWidths;
  }

  const positionedEdges: PositionedEdge[] = [];

  for (const edge of edges) {
    // Find source and target nodes
    const sourceNode = findPositionedNode(edge.source, allPositionedNodes);
    const targetNode = findPositionedNode(edge.target, allPositionedNodes);

    if (!sourceNode || !targetNode) {
      // Skip edges where nodes are not found
      continue;
    }

    // Get slot Y positions (fall back to center if not found)
    const sourceY = sourceSlots.get(edge.id) ?? (sourceNode.y + sourceNode.height / 2);
    const targetY = targetSlots.get(edge.id) ?? (targetNode.y + targetNode.height / 2);

    // Source X is right edge of source node
    const sourceX = sourceNode.x + sourceNode.width;
    // Target X is left edge of target node
    const targetX = targetNode.x;

    // Create the path
    const path = createEdgePath(sourceX, sourceY, targetX, targetY);

    // Get pre-calculated stroke width (fall back to min if not found)
    const strokeWidth = strokeWidths.get(edge.id) ?? EDGE.MIN_STROKE_WIDTH;

    positionedEdges.push({
      edge,
      path,
      strokeWidth,
    });
  }

  return positionedEdges;
}

/**
 * Calculate edge path for vertical transfer within account column
 * Used for transfers between accounts in the same column
 */
export function createVerticalTransferPath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number
): string {
  // For vertical transfers, use a different curve
  const offsetX = 50; // Curve outward to avoid overlapping nodes

  if (sourceY < targetY) {
    // Flowing downward
    return `M ${sourceX} ${sourceY} C ${sourceX + offsetX} ${sourceY}, ${targetX + offsetX} ${targetY}, ${targetX} ${targetY}`;
  } else {
    // Flowing upward
    return `M ${sourceX} ${sourceY} C ${sourceX + offsetX} ${sourceY}, ${targetX + offsetX} ${targetY}, ${targetX} ${targetY}`;
  }
}

/**
 * Determine if an edge is a vertical transfer (within account column)
 */
export function isVerticalTransfer(
  sourceNode: PositionedNode,
  targetNode: PositionedNode
): boolean {
  // Check if both nodes are in the account column (similar X position)
  const xDiff = Math.abs(sourceNode.x - targetNode.x);
  return xDiff < 50; // Within same column
}
